/**
 * Share utilities for Wordle game results
 */

/**
 * Generate shareable text from game guesses
 * @param {Array} guesses - Array of guess objects with word and evaluation
 * @param {number} attempts - Number of attempts taken
 * @param {boolean} won - Whether the player won
 * @param {string} dateString - Date string for the daily word
 * @returns {string} Formatted share text
 */
export function generateShareText(guesses, attempts, won, dateString) {
  const formattedDate = formatDateForShare(dateString);
  const result = won ? `${attempts}/6` : 'X/6';

  let shareText = `Howard Wordle ${formattedDate}\n${result}\n\n`;

  // Generate emoji grid
  guesses.forEach(guess => {
    const row = guess.evaluation.map(state => {
      switch (state) {
        case 'correct':
          return '🟩';
        case 'present':
          return '🟨';
        case 'absent':
          return '⬛';
        default:
          return '⬜';
      }
    }).join('');
    shareText += row + '\n';
  });

  shareText += '\nhttps://tribe.college/';

  return shareText;
}

/**
 * Format date string for sharing (e.g., "Jan 15, 2025")
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Formatted date
 */
function formatDateForShare(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Copy text to clipboard with fallback
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export async function copyToClipboard(text) {
  try {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    textArea.remove();

    return successful;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}
